import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputFechaComponent } from '../../../../core/components/inputs/input-fecha/input-fecha.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS } from '../../../../core/config/aggrid.default.config';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { CuentaBancariaService } from '../../../../maestros/services/maestros/cuenta-bancaria/cuenta-bancaria.service';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { CuentaBancaria } from '../../../../models/maestros/cuenta-bancaria.model';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { AppRoutes } from '../../../../shared/route-paths.const';
import { FacturaParaSepaDtoOut, SepaDtoIn, SepaForm } from '../../../models/sepa.model';
import { ContabilidadSepaService } from '../../../services/contabilidad-sepa.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
@Component({
  selector: 'app-sepa-new',
  standalone: true,
  imports: [
    FormErrorsComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    InputTextareaComponent,
    AgGridAngular,
    CommonModule,
    InputFechaComponent,
  ],
  templateUrl: './sepa-new.component.html',
  styleUrl: './sepa-new.component.scss',
})
export class SepaNewComponent implements OnInit {
  gridApi!: GridApi;
  isColumnDropdownOpen = false;

  newSepaForm!: ReturnType<typeof this.fb.group<SepaForm>>;
  isLoading = false;
  facturasParaSepa: FacturaParaSepaDtoOut[] = [];

  empresasRefacturadoras: EmpresaFacturadora[] = [];
  cuentasBancarias: CuentaBancaria[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly service: ContabilidadSepaService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService,
    private readonly cuentaBancariaService: CuentaBancariaService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.cargarTablasMaestras();
    this.setearSuscripciones();
    this.refreshGridColumns();
  }

  cargarTablasMaestras(): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);

    this.newSepaForm.get('empresaFacturadoraId')?.setValue(null);
  }

  setearSuscripciones(): void {
    this.newSepaForm.get('empresaFacturadoraId')?.valueChanges.subscribe((value) => {
      this.cuentasBancarias = [];

      //SET CUENTAS BANCARIAS
      this.cuentaBancariaService.setComboCuentasBancariasByEmpresaRefacturadoraAsync(
        this.cuentasBancarias,
        this.newSepaForm.get('empresaFacturadoraId')?.value ?? 1
      );

      //LLAMAR FACTURAS PARA SEPA
      if (value == null) {
        this.facturasParaSepa = [];
      } else {
        this.cargarFacturasParaSepa(value);
      }
    });
  }

  cargarFacturasParaSepa(empresaFacturadoraId: number): void {
    this.service.getFacturasParaSepa(empresaFacturadoraId).subscribe({
      next: (data: FacturaParaSepaDtoOut[]) => {
        this.facturasParaSepa = data;
      },
    });
  }

  crearSepa(): void {
    this.isLoading = true;
    const rawSepa = this.newSepaForm.getRawValue();
    const sepa: SepaDtoIn = ReplaceEmptyStringsWithNull.clean(rawSepa);

    this.service.crearSepa(sepa).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.toastService.success('SEPA creado correctamente');
        this.router.navigate([`${AppRoutes.CONTABILIDAD_ONDA.SEPA}/${data.id}`]);
      },
      error: (err) => {
        this.isLoading = false;
        this.handleCreateError(err);
      },
    });
  }

  private handleCreateError(err: any): void {
    if (err?.error?.errors) {
      this.backendErrorService.handleBackendErrors(err.error.errors, this.newSepaForm);
    } else {
      const errorMessage = err?.error?.message ?? 'Contacte con el administrador';
      this.toastService.error(`Error al crear el SEPA: ${errorMessage}`);
    }
  }

  initForm(): void {
    this.newSepaForm = this.fb.group<SepaForm>({
      empresaFacturadoraId: this.fb.control(null, { validators: Validators.required, nonNullable: true }),
      cuentaBancariaId: this.fb.control(null, { validators: Validators.required, nonNullable: true }),
      fechaGeneracionSepa: this.fb.control(null),
      fechaEmision: this.fb.control(null, { validators: Validators.required, nonNullable: true }),
      fechaCargo: this.fb.control(null, { validators: Validators.required, nonNullable: true }),
      fechaValidacion: this.fb.control(null),
      codAlbaran: this.fb.control(null),
      observaciones: this.fb.control(null),
      facturas: this.fb.control([], { validators: Validators.required, nonNullable: true }),
    });
  }

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    domLayout: 'autoHeight',
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    onSelectionChanged: () => this.onSelectionChanged(),
  };

  columnDefs: ColDef[] = [];

  columnOptions = [
    { field: 'id', headerName: 'ID', selected: true },
    { field: 'codFactura', headerName: 'CodFactura', selected: true },
    { field: 'codContable', headerName: 'Codigo Contable', selected: true },
    { field: 'clienteNombre', headerName: 'Cliente', selected: true },
    { field: 'suministroDireccion', headerName: 'Suministro', selected: true },
    { field: 'cuentaBancaria', headerName: 'Cuenta Bancaria', selected: true },
    { field: 'baseImponible', headerName: 'Base Imponible', selected: true },
    { field: 'importeTotal', headerName: 'Importe Total', selected: true },
  ];

  onSelectionChanged(): void {
    const selectedNodes = this.gridApi.getSelectedNodes() || [];
    const selectedIds = selectedNodes.map((node) => node.data.id);
    this.newSepaForm.controls.facturas.setValue(selectedIds);
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  toogleColumnDropdown(): void {
    this.isColumnDropdownOpen = !this.isColumnDropdownOpen;
  }

  toggleColumnaSeleccionada(option: any): void {
    option.selected = !option.selected;
    this.refreshGridColumns();
  }

  refreshGridColumns(): void {
    const id = {
      headerName: '',
      field: 'checkbox',
      width: 50,
      pinned: 'left' as const,
      checkboxSelection: true,
      headerCheckboxSelection: true,
    };

    this.columnDefs = [
      id,
      ...this.columnOptions.map((col) => ({
        field: col.field,
        headerName: col.headerName,
        hide: this.isColumnHide(col.field),
      })),
    ];
  }

  isColumnHide(columna: string): boolean {
    const column = this.columnOptions.find((col) => col.field === columna);
    return !column?.selected;
  }
}
